import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '@constants';

import MainLayout from '@layouts/MainLayout';
import AuthLayout from '@layouts/AuthLayout';
import ProtectedRoute from '@components/common/ProtectedRoute';
import FeatureProtectedRoute from '@components/common/FeatureProtectedRoute';
import PublicRoute from '@components/common/PublicRoute';
import PermissionGuard from '@components/common/PermissionGuard';

import HomePage from '@pages/Home';
import TermsAndConditions from '@pages/TermsAndConditions';
import PrivacyPolicy from '@pages/PrivacyPolicy';
import FAQPage from '@pages/FAQ';
import OurJourney from '@pages/OurJourney';
import LoginPage from '@pages/auth/Login';
import RegisterPage from '@pages/auth/Register';
import VerifyOTPPage from '@pages/auth/VerifyOTP';
import ForgotPasswordPage from '@pages/auth/ForgotPassword';
import DashboardPage from '@pages/DashboardNew';
import StudentsList from '@pages/StudentsList';
import MyConnections from '@pages/MyConnections';
import ProfilePage from '@pages/Profile';
import SettingsPage from '@pages/Settings';
import CartPage from '@pages/Cart';
import CartViewPage from '@pages/CartView';
import HistoryPage from '@pages/History';
import UploadDocumentsPage from '@pages/UploadDocuments';
import ProfileUnderVerification from '@pages/ProfileUnderVerification';
import StudentDetails from '@pages/StudentDetails';
import PortfolioSetup from '@pages/PortfolioSetup';
import AdminDashboard from '@pages/admin/AdminDashboard';
import MentorReview from '@pages/admin/MentorReview';
import MentorList from '@pages/admin/MentorList';
import AdminMentorDetails from '@pages/admin/MentorDetails';
import StudentList from '@pages/admin/StudentList';
import AdminStudentDetails from '@pages/admin/StudentDetails';
import FeedbackManagement from '@pages/admin/FeedbackManagement';
import FeedbackDetails from '@pages/admin/FeedbackDetails';
import SupportManagement from '@pages/admin/SupportManagement';
import SupportTicketDetails from '@pages/admin/SupportTicketDetails';
import QuestionManagement from '@pages/admin/QuestionManagement';
import QuestionDetails from '@pages/admin/QuestionDetails';
import PaymentManagement from '@pages/admin/PaymentManagement';
import PaymentDetails from '@pages/admin/PaymentDetails';
import MentorBalances from '@pages/admin/MentorBalances';
import AdminManagement from '@pages/admin/AdminManagement';
import SubscriptionHistory from '@pages/admin/SubscriptionHistory';
import SubscriptionDetails from '@pages/admin/SubscriptionDetails';
import ManageCarts from '@pages/admin/ManageCarts';
import StudentDashboard from '@pages/student/StudentDashboard';
import CareerMap from '@pages/student/CareerMap';
import CareerRoadmap from '@pages/student/CareerRoadmap';
import CareerReport from '@pages/student/CareerReport';
import SkillTracker from '@pages/student/SkillTracker';
import Portfolio from '@pages/student/Portfolio';
import UniversityDetails from '@pages/student/UniversityDetails';
import Mentor from '@pages/student/Mentor';
import MentorDetails from '@pages/student/MentorDetails';
import Opportunity from '@pages/student/Opportunity';
import ScoreCard from '@pages/student/ScoreCard';
import Requests from '@pages/student/Requests';
import ConnectionRequests from '@pages/Requests';
import Notes from '@pages/student/Notes';
import MyQuestions from '@pages/student/MyQuestions';
import QuestionDetail from '@pages/student/QuestionDetail';
import MentorChat from '@pages/student/MentorChat';
import PlanUpgrade from '@pages/student/PlanUpgrade';
import PaymentHistory from '@pages/PaymentHistory';
import MentorDashboard from '@pages/mentor/MentorDashboard';
import MentorQuestionDetail from '@pages/mentor/MentorQuestionDetail';
import MentorsList from '@pages/mentor/MentorsList';
import MentorProfileDetails from '@pages/mentor/MentorProfileDetails';
import MentorEarnings from '@pages/mentor/Earnings';
import MentorNotifications from '@pages/mentor/Notifications';
import Support from '@pages/Support';
import SupportTicketDetailsPage from '@pages/SupportTicketDetails';
import NotFoundPage from '@pages/errors/NotFound';
import UnauthorizedPage from '@pages/errors/Unauthorized';
import ServerErrorPage from '@pages/errors/ServerError';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTES.HOME,
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.TERMS_AND_CONDITIONS,
        element: <TermsAndConditions />,
      },
      {
        path: ROUTES.PRIVACY_POLICY,
        element: <PrivacyPolicy />,
      },
      {
        path: ROUTES.FAQ,
        element: <FAQPage />,
      },
      {
        path: ROUTES.OUR_JOURNEY,
        element: <OurJourney />,
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/support',
        element: (
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        ),
      },
      {
        path: '/support/:ticketId',
        element: (
          <ProtectedRoute>
            <SupportTicketDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/mentor-profile/:mentorId',
        element: (
          <ProtectedRoute>
            <MentorDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.CART,
        element: (
          <ProtectedRoute>
            <FeatureProtectedRoute feature="cart">
              <CartPage />
            </FeatureProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.CART_VIEW,
        element: (
          <ProtectedRoute>
            <FeatureProtectedRoute feature="cart">
              <CartViewPage />
            </FeatureProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.HISTORY,
        element: (
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PAYMENT_HISTORY,
        element: (
          <ProtectedRoute>
            <PaymentHistory />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.NOTES,
        element: (
          <ProtectedRoute>
            <FeatureProtectedRoute feature="notes">
              <Notes />
            </FeatureProtectedRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/notifications',
        element: (
          <ProtectedRoute>
            <ConnectionRequests />
          </ProtectedRoute>
        ),
      },
      {
        path: '/students',
        element: (
          <ProtectedRoute>
            <StudentsList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/students/myConnection',
        element: (
          <ProtectedRoute>
            <MyConnections />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.COMPLETE_PROFILE,
        element: (
          <ProtectedRoute>
            <UploadDocumentsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROFILE_VERIFICATION,
        element: (
          <ProtectedRoute>
            <ProfileUnderVerification />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDENT_DETAILS,
        element: (
          <ProtectedRoute>
            <StudentDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PORTFOLIO_SETUP,
        element: (
          <ProtectedRoute>
            <PortfolioSetup />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/dashboard',
        element: (
          <ProtectedRoute roles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/mentors',
        element: (
          <ProtectedRoute roles={['admin']}>
            <MentorList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/mentors/:mentorId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <AdminMentorDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/mentors/:mentorId/review',
        element: (
          <ProtectedRoute roles={['admin']}>
            <MentorReview />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/students',
        element: (
          <ProtectedRoute roles={['admin']}>
            <StudentList />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/students/:studentId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <AdminStudentDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/feedback',
        element: (
          <ProtectedRoute roles={['admin']}>
            <FeedbackManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/feedback/:feedbackId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <FeedbackDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/support',
        element: (
          <ProtectedRoute roles={['admin']}>
            <SupportManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/support/:ticketId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <SupportTicketDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/questions',
        element: (
          <ProtectedRoute roles={['admin']}>
            <QuestionManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/questions/:questionId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <QuestionDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/payments',
        element: (
          <ProtectedRoute roles={['admin']}>
            <PaymentManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/payments/:questionId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <PaymentDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/mentor-balances',
        element: (
          <ProtectedRoute roles={['admin']}>
            <MentorBalances />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/admin-management',
        element: (
          <ProtectedRoute roles={['admin']}>
            <AdminManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/subscriptions',
        element: (
          <ProtectedRoute roles={['admin']}>
            <SubscriptionHistory />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/subscriptions/:transactionId',
        element: (
          <ProtectedRoute roles={['admin']}>
            <SubscriptionDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/carts',
        element: (
          <ProtectedRoute roles={['admin']}>
            <PermissionGuard requiredPermission="VIEW_CARTS" adminOnly>
              <ManageCarts />
            </PermissionGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MENTOR_DASHBOARD,
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MENTOR_MENTORS_LIST,
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorsList />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MENTOR_PROFILE_DETAILS,
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorProfileDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MENTOR_EARNINGS,
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorEarnings />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MENTOR_NOTIFICATIONS,
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorNotifications />
          </ProtectedRoute>
        ),
      },
      {
        path: '/mentor/questions/:questionId',
        element: (
          <ProtectedRoute roles={['mentor']}>
            <MentorQuestionDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.STUDENT_DASHBOARD,
        element: (
          <ProtectedRoute roles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.STUDENT_CAREER_MAP} replace />,
          },
          {
            path: ROUTES.STUDENT_CAREER_MAP,
            element: <CareerMap />,
          },
          {
            path: ROUTES.STUDENT_CAREER_ROADMAP,
            element: <CareerRoadmap />,
          },
          {
            path: ROUTES.STUDENT_CAREER_REPORT,
            element: <CareerReport />,
          },
          {
            path: ROUTES.STUDENT_SKILL_TRACKER,
            element: <SkillTracker />,
          },
          {
            path: ROUTES.STUDENT_PORTFOLIO,
            element: <Portfolio />,
          },
          {
            path: ROUTES.STUDENT_MENTOR,
            element: <Mentor />,
          },
          {
            path: '/student/mentor/:mentorId',
            element: <MentorDetails />,
          },
          {
            path: ROUTES.STUDENT_OPPORTUNITY,
            element: <Opportunity />,
          },
          {
            path: ROUTES.STUDENT_SCORE_CARD,
            element: <ScoreCard />,
          },
          {
            path: ROUTES.STUDENT_REQUESTS,
            element: <Requests />,
          },
          {
            path: ROUTES.STUDENT_MY_QUESTIONS,
            element: <MyQuestions />,
          },
          {
            path: ROUTES.STUDENT_QUESTION_DETAIL,
            element: <QuestionDetail />,
          },
          {
            path: '/student/mentor/:mentorId/questions',
            element: <MentorChat />,
          },
          {
            path: '/student/university/:universityId',
            element: <UniversityDetails />,
          },
          {
            path: ROUTES.STUDENT_PLAN_UPGRADE,
            element: <PlanUpgrade />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: (
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        ),
      },
      {
        path: ROUTES.REGISTER,
        element: (
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        ),
      },
      {
        path: ROUTES.VERIFY_OTP,
        element: <VerifyOTPPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: <UnauthorizedPage />,
  },
  {
    path: ROUTES.SERVER_ERROR,
    element: <ServerErrorPage />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFoundPage />,
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.NOT_FOUND} replace />,
  },
]);

export default router;
